import { useObserver } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useCheckoutLineItems } from 'src/checkout/hooks/use-checkout-line-items';
import { GetDispensaryCharitiesDocument } from 'src/gql/graphql';
import type { GetDispensaryCharitiesQuery } from 'src/gql/graphql';
import useCart from 'src/hooks/use-cart';
import { useIsFullstackDispensary } from 'src/hooks/use-is-fullstack-dispensary';

type UseRoundupForCharityReturn = {
  activeCharity: GetDispensaryCharitiesQuery['getDispensaryCharities'][number] | null;
  shouldShow: boolean;
  charityRoundupSelected: boolean;
  roundupAmount: number;
};

export const useRoundupForCharity = (): UseRoundupForCharityReturn => {
  const Cart = useCart();
  const flags = useFlags();
  const isConfigurableDefaultEnabled = flags['fintech.retail-payments.rutc-self-serve.rollout'] ?? false;
  const menuType = useObserver(() => Cart.menuType);

  const { order } = Cart;
  const dispensaryId = order?.dispensary?.id;

  const charityRoundupSelected = useObserver(() => !!Cart.charityRoundupSelected);
  const charityFeeDonationId = useObserver(() => Cart.charityFeeDonationId);

  const lineItemData = useCheckoutLineItems();
  const totalValue = lineItemData.find((lineItem) => lineItem.type === 'total')?.value ?? 0;
  const roundupForCharityAmount = lineItemData.find((lineItem) => lineItem.type === 'roundupForCharity')?.value ?? 0;
  const tipValue = lineItemData.find((lineItem) => lineItem.type === 'tip')?.value ?? 0;

  // Calculate original preTipTotal by subtracting roundup if it exists in the receipt
  // (The total includes the roundup, so we need to remove it to get the original preTipTotal)
  const originalPreTipTotal = totalValue - tipValue - roundupForCharityAmount;
  const roundupAmount = Math.ceil(originalPreTipTotal) - originalPreTipTotal;

  const hasRoundupForCharityAmount = roundupAmount > 0;

  const { data: charityData, error: charityError } = useQuery(GetDispensaryCharitiesDocument, {
    skip: !dispensaryId,
    variables: {
      dispensaryId,
      menuType,
    },
    fetchPolicy: 'cache-and-network',
  });

  const activeCharity = charityData?.getDispensaryCharities.find((charity) => charity.isActive) ?? null;
  const activeCharityId = activeCharity?.feeDonationId ?? null;

  // Hydrate Cart with Charity ID and enable roundup when charity first loads
  useEffect(() => {
    if (activeCharityId && charityFeeDonationId !== activeCharityId && hasRoundupForCharityAmount) {
      Cart.setCharityFeeDonationId(activeCharityId);
      Cart.setCharityRoundupSelected(isConfigurableDefaultEnabled ? activeCharity?.defaultOptIn ?? true : true);
    } else if (!hasRoundupForCharityAmount) {
      Cart.clearCharityRoundup();
    }
  }, [
    activeCharityId,
    activeCharity?.defaultOptIn,
    Cart,
    charityFeeDonationId,
    hasRoundupForCharityAmount,
    isConfigurableDefaultEnabled,
  ]);

  const shouldShow = !charityError && !!activeCharity && hasRoundupForCharityAmount;

  return {
    activeCharity,
    shouldShow,
    charityRoundupSelected,
    roundupAmount,
  };
};

export const useIsRoundupForCharityEnabled = (): boolean => {
  const Cart = useCart();
  const charityFeeDonationId = useObserver(() => !!Cart.charityFeeDonationId);
  const charityRoundupSelected = useObserver(() => !!Cart.charityRoundupSelected);
  const isFullstackDispensary = useIsFullstackDispensary();

  // Clear roundup for charity if dispensary is not fullstack (user switches menus or dispensaries with stale cart charity data)
  useEffect(() => {
    if ((charityFeeDonationId || charityRoundupSelected) && !isFullstackDispensary) {
      Cart.clearCharityRoundup();
    }
  }, [charityFeeDonationId, charityRoundupSelected, Cart, isFullstackDispensary]);

  return isFullstackDispensary;
};
